// render-from-cache.ts — the instant cache-first render path for openItemInfo().
//
// window.ftRenderItemFromCache(id, container) tries to paint a nested object's card from the
// client caches (casterCache/objectCache) with ZERO DB/network, reusing the existing
// window.renderObjectCard(). Returns true on a clean cache render; false on any miss /
// ineligible type / not-ready renderer, so template.js falls back to the server queryData() path.
//
// It does NOT touch search-history or the side-info tab — template.js owns those (one place →
// Back/Forward stay correct). The whole body is try-wrapped so a malformed blob can never throw
// out of openItemInfo.
//
// NOTE: the side info panel intentionally does NOT show the reverse-index "attached items" section
// (that's a main item.php page concern, never the side panel). So this path renders the card only.

import { findFullNestedItem, findCachedFullItemNoWalk, resolveCachedItemForRender, isObjectCardEligibleType, isInventoryNode, computeInventoryRenderPlan, findCasterKeyForLock, stampCachedInventoryLockState } from './find-cached-item';
import { syncCachedInventoryContents, type CachedItemNode } from './find-cached-item';
import { buildNamesMapFromBlob, foldAmbientRootCatalogsIntoNames } from './build-names-map';
import { setOpenSideInfo } from './open-side-info';

type CacheRenderWindow = Window & {
    getSidelogContext?: () => unknown;
    refreshDragAndDrop?: () => void;
};

function hasViewerPermissions(blob: CachedItemNode): boolean {
    return blob.permissions !== undefined && blob.permissions !== null;
}

// A STATIC item-creature's equipped-gear projection: a synthetic (md5-derived, no-DB) inventory node
// the server bakes into the creature blob (enchantment_and_inventory_layering.php), already
// enchantment-composed (computed.enchantment). Its inventory_id exists in no table, so a SideInfo click
// must NOT punt to the server (discern_item_id would 404) — it renders read-only from the cached blob.
// This is a NARROW carve-out of the missing-permissions → server rule, NOT a general relaxation: it
// applies only to nodes the server explicitly stamped inventory_static_display=1.
function isStaticDisplayInventoryNode(blob: CachedItemNode): boolean {
    const raw = (blob as { inventory?: unknown } | null)?.inventory;
    const inv = Array.isArray(raw) ? raw[0] : raw;
    if (!inv || typeof inv !== 'object') return false;
    const v = (inv as Record<string, unknown>).inventory_static_display;
    return v === 1 || v === '1' || v === true;
}

/**
 * Attempt an instant cache render of `id` into `container`. Returns true on success (caller skips
 * the server round-trip), false to fall back to the existing queryData() path.
 */
export function ftRenderItemFromCache(id: string, container: HTMLElement): boolean {
    try {
        if (!id || !container) return false;
        // resolveCachedItemForRender = findFullNestedItem + thinned-inventory-instance rehydration: a
        // chest/coin/gear whose template was de-duped to the root catalog (no own item_layer_types) is
        // resolved by inventory_id and merged with its catalog template def, so it renders client-side
        // instead of falling back to the server. Null only when truly uncached / un-rehydratable.
        const blob = resolveCachedItemForRender(id);
        if (!blob) return false;                       // shell-only or not cached → server
        if (!isObjectCardEligibleType(blob)) return false; // creature/PC/status-instance/etc → server

        // Inventory: render the cached full-nest node instantly UNLESS it carries a control that needs
        // server-only data (GM door/light/translate tools, lockpick, gather, or the encounter-within-
        // reach disenchant case) — those fall back so the server renders the full, correct control set.
        // (Book read/unread + encounter links no longer punt: they're server-island fragments the TS
        // card hydrates — islands.ts, OC-1.) The plan also tells the renderer whether to paint the
        // disenchant button (carried enchanting tools), which we stamp on the blob for
        // renderInventoryControls to read.
        if (isInventoryNode(blob)) {
            // Heal-on-access [unified-viewer-scoping Chunk 3]: the WS broadcast strips inventory
            // permissions, and a genuinely-new node (post-load loot drop / first-sight) has no prior
            // cached node to carry them forward from, so it arrives WITHOUT `permissions`. The seed /
            // per-viewer object endpoint ALWAYS stamps a permissions object, so an ABSENT key uniquely
            // marks such a stripped node — fall back to the server, whose per-viewer SSR (table_item_
            // object) renders the correct gates. A later seed/resync re-stamps it and it cache-renders
            // again. Fail-safe: routing to the server is always correct, never an escalation.
            //
            // EXCEPT on the modify_encounter tools page: that page is PHP-authorized to edit the whole
            // encounter, but its server inventory card has NO tools-page edit-control escape hatch, and
            // an adventure encounter has no campaign GM to heal toward — so a permissions-stripped node
            // (e.g. after a delete's WS broadcast strips them) would server-render a CONTROL-LESS card,
            // and the user loses the edit console for every item until a full reload. Keep rendering from
            // cache here; renderInventoryControls' own ftIsModifyEncounterToolsPage() hatch supplies the
            // controls. Player-facing pages keep the original server-heal behavior.
            // Static item-creature equipped-gear projection: render read-only from cache even without a
            // permissions bucket (its synthetic inventory_id would 404 server-side). Narrow — gated on
            // the server's inventory_static_display marker only.
            const isStaticDisplay = isStaticDisplayInventoryNode(blob);
            if (
                !hasViewerPermissions(blob)
                && !(typeof ftIsModifyEncounterToolsPage === 'function' && ftIsModifyEncounterToolsPage())
                && !isStaticDisplay
            ) {
                return false;
            }
            const host = window as CacheRenderWindow;
            let sideCtx: unknown = null;
            try {
                if (typeof host.getSidelogContext === 'function') {
                    sideCtx = host.getSidelogContext();
                }
            } catch (e) { sideCtx = null; }
            const plan = computeInventoryRenderPlan(blob, sideCtx);
            // A static-display projection has no persisted row and thus no server-only controls to fetch;
            // never punt it to the server (it would 404), always paint it read-only from cache.
            if (plan.server && !isStaticDisplay) return false;
            try { blob._ft_oc_flags = { showDisenchant: plan.showDisenchant }; } catch (e) { /* non-fatal */ }
        }

        // NOTE: no ftWarnIfShapeStale here — `blob` is a NESTED sub-item, which never carries its own
        // _meta.shape_v (only root caster/object blobs are stamped). The shape tripwire belongs at the
        // cache-FILL site (caster-cache.ts), not per nested render.

        if (typeof window.renderObjectCard !== 'function') return false; // bundle not ready → server
        // Names harvested from the blob itself, then gap-filled from the cached ROOT catalogs: a
        // nested creature-held inventory carries no catalog of its own, but its charged_spell /
        // charged_ability defs live in the creature/encounter root catalog (baked there by
        // ft_bake_castable_into_blob). Without the fold, "Charged Spell:" rendered a bare UUID. (See
        // foldAmbientRootCatalogsIntoNames.)
        const names = foldAmbientRootCatalogsIntoNames(buildNamesMapFromBlob(blob));
        const ok = window.renderObjectCard(blob, names, container);
        if (!ok) return false;                         // renderer signalled not-ready → server

        // Hydrate the painted card, scoped to the container. renderObjectCard already runs runes /
        // thumbnails / tables / inventory tiles, but NOT ftMountIcons / ftMountParsedText — and the
        // header type-badge (iconMap('<ability_type>')) falls back to an inert <i class="ft-icon">
        // placeholder when the rune bundle isn't ready at paint time, which only ftMountIcons upgrades.
        // The server path runs these after queryData; we must run them here since we return early.
        if (typeof window.ftMountIcons === 'function') { try { window.ftMountIcons(container); } catch (e) { /* footer pass covers */ } }
        if (typeof window.ftMountRuneIcons === 'function') { try { window.ftMountRuneIcons(container); } catch (e) { /* footer pass covers */ } }
        if (typeof window.ftMountThumbnails === 'function') { try { window.ftMountThumbnails(container); } catch (e) { /* footer pass covers */ } }
        if (typeof window.ftMountCarousels === 'function') { try { window.ftMountCarousels(container); } catch (e) { /* footer pass covers */ } }
        if (typeof window.ftMountParsedText === 'function') { try { window.ftMountParsedText(container); } catch (e) { /* footer pass covers */ } }
        if (typeof window.ftMountParseTextTables === 'function') { try { window.ftMountParseTextTables(container); } catch (e) { /* footer pass covers */ } }
        // Container-contents tiles (and the move dropzone) are injected by ftMountInventoryTiles above, but
        // drag is bound per-element by refreshDragAndDrop() (`.draggable:not(.drag-initialized)` /
        // `.dropzone:not(.dropzone-feedback)`), NOT by delegation — so freshly-painted tiles can't be
        // dragged until it re-runs. The PC sheet calls it after every repaint; mirror that here. Idempotent.
        const ftRefreshDragAndDrop = (window as CacheRenderWindow).refreshDragAndDrop;
        if (typeof ftRefreshDragAndDrop === 'function') { try { ftRefreshDragAndDrop(); } catch (e) { /* non-fatal */ } }

        // Register what the side-info drawer is now showing so a later websocket/invalidation that
        // touches this id can re-render it in place (see open-side-info.ts). We register by the id
        // the caller asked for — for an inventory instance that's its inventory_id, matching how the
        // affected-id sets are keyed.
        try { setOpenSideInfo(id, container.id); } catch (e) { /* non-fatal */ }

        return true;
    } catch (e) {
        console.warn('ftRenderItemFromCache failed', e);
        return false;
    }
}

// ftFindFullNestedItem: window-exposed finder so non-bundle callers (template.js
// openEditInventoryForm) can pull a cached full-nest inventory/object node and push it to a
// server form instead of forcing a second DB build. Same lookup the cache render uses above.
//
// ftResolveCachedItemForRender: the SAME resolver ftRenderItemFromCache uses (findFullNestedItem +
// thinned-inventory-instance rehydration + enchantment-template synth). Exposed so the Edit Inventory
// opener (inventory-actions openEditInventoryForm) resolves a DEDUPED instance — a map marker / door
// whose shared template was thinned to the root catalog — the SAME way the card it opens from does.
// Using the raw findFullNestedItem there returned null for those (isFull fails on a layerless thinned
// node), so the form silently bailed to its placeholder even though the card had rendered fine.
//
// ftFindCasterKeyForLock: the "JSON truth" key↔lock lookup — does the active caster carry a key
// whose computed.key_links.opens includes one of the given lock ids? Exposed for useKeysOnLock
// (lockpick.ts, a different bundle): it resolves the matching key CLIENT-SIDE and sends it
// explicitly, so the server never has to search — and a guaranteed miss short-circuits with no
// AJAX at all.
//
// ftStampCachedInventoryLockState: after use_key_inc.php confirms an unlock/relock, useKeysOnLock
// stamps the new lock state onto every cached copy of the row so the immediate panel refresh
// renders the CONFIRMED state instead of the stale pre-action blob (the WS recompile that
// replaces the cache wholesale lands ~1s later).
Object.assign(window, {
    ftSyncCachedInventoryContents: syncCachedInventoryContents,
    ftRenderItemFromCache,
    ftFindFullNestedItem: (id: string) => findFullNestedItem(id),
    ftFindCachedFullItemNoWalk: findCachedFullItemNoWalk,
    ftResolveCachedItemForRender: resolveCachedItemForRender,
    ftFindCasterKeyForLock: findCasterKeyForLock,
    ftStampCachedInventoryLockState: stampCachedInventoryLockState,
});
